import { motion } from "framer-motion";
import { TIER_TEASERS, TIER_GARMENT_PREVIEW } from "@/data/tierPreviews";

/**
 * RoadAheadCard — small "next tier" preview with garment silhouette.
 * Used on graduation card, on the Journey page, and as a standalone preview popover.
 *
 * Props:
 *  - nextTierName: string — name of the upcoming tier (e.g., "Sandalphon")
 *  - variant: 'compact' | 'full' (default 'compact')
 *  - title: string — heading text (default 'The road ahead')
 */
const RoadAheadCard = ({ nextTierName, variant = "compact", title = "The road ahead" }) => {
  const teaser = TIER_TEASERS[nextTierName];
  const garment = TIER_GARMENT_PREVIEW[nextTierName];
  if (!teaser || !nextTierName) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`rounded-xl text-left ${variant === "full" ? "p-5" : "p-3"}`}
      style={{
        background: "linear-gradient(135deg, rgba(79,55,139,0.08) 0%, rgba(212,165,116,0.08) 100%)",
        border: "1px dashed rgba(79,55,139,0.25)"
      }}
      data-testid="road-ahead-card"
    >
      <div
        className="text-[10px] font-bold uppercase tracking-widest mb-2"
        style={{ color: "#8B7DA8", fontFamily: "'Cinzel', serif" }}
      >
        {title}
      </div>
      <div className="flex items-start gap-3">
        {/* Tier emoji */}
        <span className={variant === "full" ? "text-3xl flex-shrink-0" : "text-xl flex-shrink-0"}>
          {teaser.emoji}
        </span>
        <div className="flex-1 min-w-0">
          <div
            className={variant === "full" ? "text-base font-bold leading-tight" : "text-sm font-bold leading-tight"}
            style={{ color: "#4F378B", fontFamily: "'Fredoka', sans-serif" }}
          >
            Next: {nextTierName}
          </div>
          <div
            className={`italic mt-0.5 ${variant === "full" ? "text-sm" : "text-xs"}`}
            style={{ color: "#5C5470", fontFamily: "'Nunito', sans-serif" }}
          >
            {teaser.line}
          </div>
        </div>

        {/* Garment silhouette — fogged glass preview */}
        {garment && (
          <div className="flex-shrink-0 flex flex-col items-center" data-testid="garment-silhouette">
            <div
              className={variant === "full" ? "w-16 h-16" : "w-12 h-12"}
              style={{
                position: "relative",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at 35% 35%, rgba(255,255,255,0.5), rgba(123,94,167,0.18) 60%, rgba(79,55,139,0.28))",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow:
                  "inset 0 0 12px rgba(255,255,255,0.35), 0 4px 14px rgba(79,55,139,0.18)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {/* Frosted overlay for the silhouette feel */}
              <span
                className={variant === "full" ? "text-3xl" : "text-2xl"}
                style={{
                  filter: "blur(1.4px) opacity(0.7) drop-shadow(0 1px 2px rgba(79,55,139,0.5))",
                  WebkitFilter: "blur(1.4px) opacity(0.7) drop-shadow(0 1px 2px rgba(79,55,139,0.5))"
                }}
                aria-hidden="true"
              >
                {garment.emoji}
              </span>
              {/* Soft frost sheen */}
              <div
                style={{
                  position: "absolute", inset: 0, pointerEvents: "none", borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.35) 0%, transparent 45%, transparent 55%, rgba(79,55,139,0.08) 100%)"
                }}
              />
            </div>
            <div
              className="text-[9px] mt-1 text-center max-w-[70px] leading-tight"
              style={{ color: "#8B7DA8", fontFamily: "'Nunito', sans-serif", fontStyle: "italic" }}
            >
              garment locked
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RoadAheadCard;
